import {
  IProductionDashboardAssembly,
  IProductionDashboardBayVehicle,
  IProductionDashboardBayVehicleAssembly,
  ProductionDashboardAssemblyKind,
} from "../models/ProductionDashboard";

/** The API's catch-all bucket for parts outside every assembly - the one row with no assembly id. */
export const isNonAssembly = (a: IProductionDashboardBayVehicleAssembly): boolean =>
  a.partAssemblyId === null;

/**
 * The vehicle's assemblies as the dashboard shows them. TME customers don't use the Non-Assembly
 * bucket (the flow drops that column for them too), so it is left out of their stepper, done list
 * and time-left estimate rather than showing a step nobody works.
 */
export function displayedAssemblies(
  vehicle: Pick<IProductionDashboardBayVehicle, "assemblies">,
  hideNonAssembly: boolean
): IProductionDashboardBayVehicleAssembly[] {
  const own = vehicle.assemblies ?? [];
  return hideNonAssembly ? own.filter((a) => !isNonAssembly(a)) : own;
}

export type AssemblyStepState = "done" | "current" | "todo";

export interface IAssemblyStep {
  label: string;
  state: AssemblyStepState;
}

type SteppedVehicle = Pick<
  IProductionDashboardBayVehicle,
  "currentAssemblyIndex" | "assemblyCount" | "assemblies"
>;

/**
 * The strip-down of one vehicle as a row of steps, for the bay card's stepper.
 *
 * The overview's per-vehicle assembly list is the source: it names each assembly the vehicle
 * actually goes through and says which are finished. "Done" follows isComplete rather than
 * position, so an assembly skipped over (its timer never started, parts still on) reads as still
 * to do even though the vehicle has moved past it.
 *
 * The fallback for a payload without that list (an older API) is positional: numbered steps from
 * currentAssemblyIndex out of assemblyCount, named only when the customer's ordered assemblies
 * happen to line up with that count.
 */
export function assemblySteps(
  vehicle: SteppedVehicle,
  assemblies: IProductionDashboardAssembly[]
): IAssemblyStep[] {
  const own = vehicle.assemblies ?? [];
  if (own.length > 0) {
    return own.map((a) => ({
      label: a.name,
      state: a.isCurrent && !a.isComplete ? "current" : a.isComplete ? "done" : "todo",
    }));
  }

  const count = vehicle.assemblyCount ?? 0;
  if (count <= 0) return [];

  const names = assemblies
    .filter((a) => a.kind === ProductionDashboardAssemblyKind.Assembly)
    .map((a) => a.name);
  const useNames = names.length === count;
  const current = vehicle.currentAssemblyIndex ?? 0;

  return Array.from({ length: count }, (_, i) => {
    const position = i + 1;
    const state: AssemblyStepState =
      position < current ? "done" : position === current ? "current" : "todo";
    return { label: useNames ? names[i] : String(position), state };
  });
}

/** Names of the assemblies finished so far, in flow order. */
export function completedAssemblyNames(vehicle: SteppedVehicle): string[] {
  return (vehicle.assemblies ?? []).filter((a) => a.isComplete).map((a) => a.name);
}
